import { resolveElement } from './dom';
import { getDomain, phoneToHref } from './string';
import { MarkerClusterer, GridAlgorithm } from '@googlemaps/markerclusterer';

const DEFAULT_CONTROLS = {
  zoomIn: '.map__zoom-in',
  zoomOut: '.map__zoom-out',
};

function popupTemplate(place) {
  const title = place?.title || place?.name || '';

  const addresses = Array.isArray(place.addresses) ? place.addresses : [];
  const phones = Array.isArray(place.phones) ? place.phones : [];
  const instagrams = Array.isArray(place.instagrams) ? place.instagrams : [];
  const websites = Array.isArray(place.websites) ? place.websites : [];

  let html = `
  <div class="map__popup">
    <div class="map__info">`;

  html += `<p class="map__title">${title}</p>`;

  addresses.forEach((addr) => {
    html += `<p class="map__contact">${addr}</p>`;
  });

  phones.forEach((phone) => {
    html += `<a href="${phoneToHref(phone)}" class="map__contact">${phone}</a>`;
  });

  instagrams.forEach((ig) => {
    const handle = String(ig).replace(/^@/, '');
    html += `<a href="https://www.instagram.com/${handle}/" class="map__contact" target="_blank" rel="nofollow noopener noreferrer">@${handle}</a>`;
  });

  websites.forEach((url) => {
    html += `<a href="${url}" class="map__contact" target="_blank" rel="nofollow noopener noreferrer">${getDomain(url)}</a>`;
  });

  html += `
    </div>
  </div>`;
  return html;
}

// Compute bounds from an array of [lng, lat]
function computeBounds(coordsArray) {
  const [lng0, lat0] = coordsArray[0];
  let minLng = lng0,
    maxLng = lng0,
    minLat = lat0,
    maxLat = lat0;
  for (const [lng, lat] of coordsArray) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
}

// Pad bounds by a fraction of their span (uniform)
function padBounds(bounds, fraction = 0.1) {
  const [[minLng, minLat], [maxLng, maxLat]] = bounds;
  const spanLng = Math.max(1e-9, maxLng - minLng);
  const spanLat = Math.max(1e-9, maxLat - minLat);
  const padLng = spanLng * fraction;
  const padLat = spanLat * fraction;
  return [
    [minLng - padLng, minLat - padLat],
    [maxLng + padLng, maxLat + padLat],
  ];
}

// Pad bounds by per-side fractions: { top, right, bottom, left } or number (uniform)
function padBoundsBySides(bounds, padding = 0) {
  const [[minLng, minLat], [maxLng, maxLat]] = bounds;
  const spanLng = Math.max(1e-9, maxLng - minLng);
  const spanLat = Math.max(1e-9, maxLat - minLat);

  const sides =
    typeof padding === 'number'
      ? { top: padding, right: padding, bottom: padding, left: padding }
      : padding || {};

  const { top = 0, right = 0, bottom = 0, left = 0 } = sides;

  const newMinLng = minLng - left * spanLng;
  const newMaxLng = maxLng + right * spanLng;
  const newMinLat = minLat - bottom * spanLat; // south
  const newMaxLat = maxLat + top * spanLat; // north

  return [
    [newMinLng, newMinLat],
    [newMaxLng, newMaxLat],
  ];
}

function toLatLngLiteral(centerLngLat) {
  const [lng, lat] = centerLngLat || [0, 0];
  return { lat, lng };
}

// Simple DOM for cluster icon
function clusterCircle(count) {
  const circle = document.createElement('div');
  circle.classList.add('map__marker', 'map__marker--clusterer');
  circle.innerHTML = `<span class="map__marker-text">${count}</span>`;
  return circle;
}

// Create marker DOM (wrap, button, popup DOM string inserted)
function createMarkerDom(place) {
  const markerWrap = document.createElement('div');
  markerWrap.className = 'map__marker-wrap';

  const markerButton = document.createElement('button');
  markerButton.setAttribute('type', 'button');
  markerButton.className = 'map__marker';
  markerButton.innerHTML = `
    <svg class="map__marker-icon" width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M12 9L6 9" stroke="#fff" stroke-width="1.6" />
      <path class="plus" d="M9 6V12" stroke="#fff" stroke-width="1.6" />
    </svg>`;
  markerWrap.appendChild(markerButton);

  if (place) {
    markerButton.insertAdjacentHTML('afterend', popupTemplate(place));
  }
  const popup = markerButton.nextElementSibling;

  return { markerWrap, markerButton, popup };
}

export class ClinicsGoogleMap {
  constructor(mapElement, options = {}) {
    this.mapElement = mapElement;

    this.options = {
      controls: { ...DEFAULT_CONTROLS, ...(options.controls || {}) },
      defaultLocation: options.defaultLocation || {
        center: [54.8, 24.5],
        zoom: 9,
      },

      // Google styling
      googleMapId: options.googleMapId || 'DEMO_MAP_ID',

      // Responsive per-side fit padding (fractions of bounds span)
      fitPadding: options.fitPadding || {
        mobile: { top: 0.08, right: 0.08, bottom: 0.3, left: 0.08 },
        desktop: { top: 0.08, right: 0.08, bottom: 0.18, left: 0.08 },
      },
      fitPaddingBreakpoint: options.fitPaddingBreakpoint ?? 1024,
      singleMarkerZoom: options.singleMarkerZoom ?? 14,
      fitAnimateMs: options.fitAnimateMs ?? 300,

      // Clusterer options
      cluster: {
        enabled: options.cluster?.enabled ?? true,
        gridSize: options.cluster?.gridSize ?? 64, // pixels
      },
    };

    this.map = null;
    this.mm = null;
    this.zoomInBtn = null;
    this.zoomOutBtn = null;

    // { marker, markerWrap, markerButton, popup, clickListener, handleOutsideClick }
    this._markers = [];

    // Clusterer bookkeeping
    this._clusterEnabled = !!this.options.cluster.enabled;
    this._clusterer = null;

    // zIndex management so opened popup stays on top
    this._zTop = 10000; // start high to beat cluster icons and others

    // Imported Google classes via importLibrary
    this._g = {
      Map: null,
      AdvancedMarkerElement: null,
      LatLngBounds: null,
    };

    this._bound = {
      onZoomIn: null,
      onZoomOut: null,
      onCityChange: null,
    };

    this._mapReady = null;
  }

  async init() {
    const [{ Map }, { AdvancedMarkerElement }, { LatLngBounds }] =
      await Promise.all([
        google.maps.importLibrary('maps'),
        google.maps.importLibrary('marker'),
        google.maps.importLibrary('core'),
      ]);

    this._g.Map = Map;
    this._g.AdvancedMarkerElement = AdvancedMarkerElement;
    this._g.LatLngBounds = LatLngBounds;

    const { center, zoom } = this.options.defaultLocation || {};
    const latLng = toLatLngLiteral(center);

    this.map = new this._g.Map(this.mapElement, {
      center: latLng,
      zoom: zoom ?? 10,
      disableDefaultUI: true,
      draggable: true,
      gestureHandling: 'cooperative',
      mapId: this.options.googleMapId,
    });

    // Resolve once the map has finished first render
    this._mapReady = new Promise((resolve) => {
      const once = this.map.addListener('idle', () => {
        try {
          once.remove();
        } catch {}
        resolve();
      });
    });

    this._bindControls();

    return this;
  }

  destroy() {
    // Unbind controls
    if (this.zoomInBtn && this._bound.onZoomIn) {
      this.zoomInBtn.removeEventListener('click', this._bound.onZoomIn);
    }
    if (this.zoomOutBtn && this._bound.onZoomOut) {
      this.zoomOutBtn.removeEventListener('click', this._bound.onZoomOut);
    }

    this.clearPlaces();
    this._destroyClusterer();

    if (this.mm && this.mm.revert) {
      this.mm.revert();
    }
    this.mm = null;
    this.map = null;
  }

  _destroyClusterer() {
    if (this._clusterer) {
      try {
        this._clusterer.clearMarkers();
      } catch {}
      this._clusterer = null;
    }
  }

  clearPlaces() {
    // Remove listeners and detach markers
    for (const m of this._markers) {
      try {
        if (m.handleOutsideClick) {
          document.removeEventListener('mousedown', m.handleOutsideClick);
        }
        if (m.clickListener && typeof m.clickListener.remove === 'function') {
          m.clickListener.remove();
        }
        if (m.marker) {
          m.marker.map = null; // AdvancedMarkerElement: detach
        }
      } catch {}
    }
    this._markers = [];

    // Clear clusterer
    this._destroyClusterer();
  }

  async showPlaces(places = []) {
    this.clearPlaces();

    const markerCoords = [];
    const willCluster = this._clusterEnabled;

    for (const place of places) {
      const position = { lat: place.location.lat, lng: place.location.lng };
      const title = String(place?.title || place?.name || '');

      const { markerWrap, markerButton, popup } = createMarkerDom(place);

      const markerOptions = {
        position,
        content: markerWrap,
        gmpDraggable: false,
        gmpClickable: true,
        zIndex: 0,
        title,
      };

      // Optional: try to hint collisions so the active one isn't obscured
      try {
        markerOptions.collisionBehavior =
          google.maps.CollisionBehavior.REQUIRED_AND_HIDES_OPTIONAL;
      } catch {}

      // If clustering is disabled, attach directly to map
      if (!willCluster) {
        markerOptions.map = this.map;
      }

      const marker = new this._g.AdvancedMarkerElement(markerOptions);

      const openPopup = () => {
        // bring to top
        marker.zIndex = ++this._zTop;
        markerWrap.classList.add('is-opened');
        popup && popup.classList.add('is-shown');
      };

      const closePopup = () => {
        marker.zIndex = 0;
        markerWrap.classList.remove('is-opened');
        popup && popup.classList.remove('is-shown');
      };

      const onMarkerClick = () => {
        const isOpened = markerWrap.classList.contains('is-opened');
        if (!isOpened) {
          openPopup();
        } else {
          closePopup();
        }
      };

      // Use AdvancedMarkerElement's click event (accessible + keyboard)
      const clickListener = marker.addListener('click', onMarkerClick);

      // Close when clicking outside
      const handleOutsideClick = (event) => {
        if (
          popup &&
          !popup.contains(event.target) &&
          !markerButton.contains(event.target)
        ) {
          closePopup();
        }
      };
      document.addEventListener('mousedown', handleOutsideClick);

      this._markers.push({
        marker,
        markerWrap,
        markerButton,
        popup,
        clickListener,
        handleOutsideClick,
      });

      markerCoords.push([position.lng, position.lat]);
    }

    // Cluster if enabled
    if (willCluster && this._markers.length) {
      this._destroyClusterer();

      const markers = this._markers.map((m) => m.marker);

      const renderer = {
        render: ({ count, position }) => {
          const el = clusterCircle(count);
          // lower than any opened marker (_zTop starts at 10000)
          return new this._g.AdvancedMarkerElement({
            position,
            content: el,
            zIndex: 100, // keep clusters below opened markers
            gmpClickable: true,
          });
        },
      };

      this._clusterer = new MarkerClusterer({
        map: this.map,
        markers,
        algorithm: new GridAlgorithm({
          gridSize: this.options.cluster.gridSize,
        }),
        renderer,
      });
    }

    // Fit view to all markers
    this._fitToMarkersGmap(this.map, markerCoords, {
      padding: this._getFitPadding(),
      singleZoom: this.options.singleMarkerZoom,
    });
  }

  _getFitPadding() {
    const isMobile = window.innerWidth < this.options.fitPaddingBreakpoint;
    const cfg = this.options.fitPadding || {};
    const mobile = cfg.mobile || {};
    const desktop = cfg.desktop || {};
    return isMobile ? mobile : desktop;
  }

  _bindControls() {
    this.zoomInBtn = resolveElement(this.options.controls.zoomIn);
    this.zoomOutBtn = resolveElement(this.options.controls.zoomOut);

    this._bound.onZoomIn = () => this._changeZoom(1);
    this._bound.onZoomOut = () => this._changeZoom(-1);

    if (this.zoomInBtn)
      this.zoomInBtn.addEventListener('click', this._bound.onZoomIn);
    if (this.zoomOutBtn)
      this.zoomOutBtn.addEventListener('click', this._bound.onZoomOut);
  }

  _getZoom() {
    if (!this.map) return this.options.defaultLocation?.zoom ?? 10;
    try {
      const z = this.map.getZoom?.();
      if (typeof z === 'number') return z;
    } catch {}
    return this.options.defaultLocation?.zoom ?? 10;
  }

  _applyLocation(location) {
    if (!this.map || !location) return;
    const { center, zoom, bounds } = location;

    if (bounds && Array.isArray(bounds)) {
      const gBounds = this._boundsArrayToLatLngBounds(bounds);
      this.map.fitBounds(gBounds);
      return;
    }
    if (center && Array.isArray(center)) {
      this.map.setCenter(toLatLngLiteral(center));
    }
    if (typeof zoom === 'number') {
      this.map.setZoom(zoom);
    }
  }

  _changeZoom(delta) {
    const current = this._getZoom();
    const nextZoom = Math.max(2, Math.min(21, current + delta));
    this._applyLocation({ zoom: nextZoom });
  }

  _boundsArrayToLatLngBounds(bounds) {
    const [[minLng, minLat], [maxLng, maxLat]] = bounds;
    return new this._g.LatLngBounds(
      { lat: minLat, lng: minLng },
      { lat: maxLat, lng: maxLng },
    );
  }

  _fitToMarkersGmap(map, coords, options = {}) {
    const { padding, paddingFraction, singleZoom = 15 } = options;

    if (!coords || coords.length === 0 || !map) return;

    if (coords.length === 1) {
      map.setCenter(toLatLngLiteral(coords[0]));
      map.setZoom(singleZoom);
      return;
    }

    const rawBounds = computeBounds(coords);
    const paddedBounds =
      padding != null
        ? padBoundsBySides(rawBounds, padding)
        : padBounds(rawBounds, paddingFraction ?? 0.1);

    const gBounds = this._boundsArrayToLatLngBounds(paddedBounds);
    map.fitBounds(gBounds);
  }
}
